// @ts-strict-ignore
import React from 'react'
import { useAtomValue } from 'jotai'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Box as Card,
  Box as Container,
  DataListItem,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { floorWithPrecision, numberWithCommas } from 'utils/utils'
import { convertFromWalletAtom, convertToWalletAtom, quoteAtom } from './atoms/atoms'
import { QuoteCountdown } from './components/QuoteCountdown'
import { useConvertReview } from './hooks/useConvertReview'

export const ConvertReview = () => {
  const { t } = useTranslation()
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const quote = useAtomValue(quoteAtom)
  const [walletAction] = useWalletActionContext()
  const {
    requestError,
    isRefreshingQuote,
    isAcceptingQuote,
    handleAcceptQuote,
    handleRefreshQuote,
  } = useConvertReview()

  return (
    <Container width="full" data-testid="review-container">
      {requestError?.show && (
        <Box paddingT={4} paddingB={16}>
          <RequestErrorCallout requestError={requestError} />
        </Box>
      )}
      <Card paddingT={24} paddingB={12}>
        <DataListItem
          id="you-will-get"
          title={t('wallets.convert.youWilGet')}
          body={`${quote?.amountOut} ${quote?.to}`}
          size="large"
        />

        <DataListItem
          id="currency-price"
          title={t('wallets.convert.currencyPrice', {
            currency: convertToWallet?.currency?.code,
          })}
          body={`1 ${convertToWallet?.currency?.code} = ${floorWithPrecision(
            quote?.price,
            convertFromWallet?.currency?.pricePrecision
          )} ${convertFromWallet?.currency?.code}`}
          size="large"
        />

        <DataListItem
          id="fee"
          title={t('wallets.convert.fee')}
          body={`${quote?.fee} ${quote?.from}`}
          size="large"
        />

        <DataListItem
          id="total"
          title={t('wallets.convert.total')}
          body={`${numberWithCommas(
            floorWithPrecision(quote?.amountDue, convertFromWallet?.currency?.pricePrecision)
          )} ${convertFromWallet?.currency?.code}`}
          size="large"
        />

        <DataListItem
          id="pay-with"
          title={t('wallets.convert.payWith')}
          body={convertFromWallet?.currency?.code}
          size="large"
        />

        <QuoteCountdown
          quote={quote}
          isRefreshingQuote={isRefreshingQuote}
          onExpire={handleRefreshQuote}
        />
      </Card>
      <Callout variant="info">
        <ul className="list-disc px-4">
          <li>
            {t('wallets.convert.convertMessage', {
              toCurrency: convertToWallet?.currency?.code,
              fromCurrency: convertFromWallet?.currency?.code,
            })}
          </li>
          <li>
            <Trans
              i18nKey="wallets.convert.convertCryptoDisclaimer"
              components={{
                link1: <ExternalLink href={process.env.MICA_CRYPTO_ASSET_RISK_DISCLOSURES} />,
                link2: <ExternalLink href={process.env.WHITE_PAPERS} />,
              }}
            />
          </li>
        </ul>
      </Callout>
      <Box flex justify="between" paddingY={24}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="convert-now"
          type="button"
          variant="primary"
          onClick={handleAcceptQuote}
          loading={isAcceptingQuote || isRefreshingQuote}
        >
          {t('wallets.convert.convertNow')}
        </Button>
      </Box>
    </Container>
  )
}
